// src/scenes/LeaderboardScene.js
// ─────────────────────────────────────────────────────────────────────────────
// Two-tab leaderboard: CHALLENGE (sum of best scores) | INFINITE (best run).
// Shows top 10 globally. If the player is outside top 10, a separator row +
// their own entry (with real rank) is appended below.
// ─────────────────────────────────────────────────────────────────────────────

import Phaser from 'phaser';
import { LevelStore }                                     from '../game/levelStore.js';
import { fetchTopChallenge, fetchTopInfinite,
         fetchChallengeRank, fetchInfiniteRank,
         submitChallenge, submitInfinite }                from '../services/leaderboard.js';
import { getCountryCode, toFlag }                         from '../services/geo.js';
import { I18n }                                           from '../game/I18n.js';
import NameBuilderScene                                   from './NameBuilderScene.js';

// ── Layout ────────────────────────────────────────────────────────────────────
const HEADER_H = 62;
const TAB_H    = 48;
const LIST_Y   = HEADER_H + TAB_H;   // 110
const TOP_N    = 10;
const ROW_H    = 52;
const D        = 5;

export default class LeaderboardScene extends Phaser.Scene {
  constructor() { super('LeaderboardScene'); }

  init(data) {
    this.fromScene = data?.from || 'MapScene';
    this.activeTab = data?.tab  || 'challenge';
  }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;

    this.add.rectangle(W / 2, H / 2, W, H, 0x05080f, 1);

    this._drawHeader(W);
    this._drawTabs(W);
    this._drawPlayerSection(W, H);

    // Loading indicator in list area
    this._loadingText = this.add.text(W / 2, LIST_Y + (ROW_H * TOP_N) / 2, I18n.t('loading'), {
      fontFamily: 'system-ui, Arial', fontSize: '14px', color: '#334155',
    }).setOrigin(0.5).setDepth(D);

    this._fetchAndRender();
    getCountryCode().catch(() => {});
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  _drawHeader(W) {
    this.add.rectangle(W / 2, HEADER_H / 2, W, HEADER_H, 0x08090f, 0.97).setDepth(D);
    this.add.rectangle(0, HEADER_H, W, 2, 0xfbbf24, 0.5).setOrigin(0, 0).setDepth(D + 1);

    this.add.text(W / 2, HEADER_H / 2, '🏆  LEADERBOARD', {
      fontFamily: 'system-ui, Arial', fontSize: '17px',
      color: '#fbbf24', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 1);

    const close = this.add.rectangle(W - 32, HEADER_H / 2, 48, 28, 0x1f2937, 0.9)
      .setStrokeStyle(1.5, 0xef4444, 0.7).setDepth(D + 1)
      .setInteractive({ useHandCursor: true });
    this.add.text(W - 32, HEADER_H / 2, I18n.t('lb_close'), {
      fontFamily: 'system-ui, Arial', fontSize: '10px', color: '#ef4444', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    close.on('pointerover', () => close.setFillStyle(0x3f0000, 0.9));
    close.on('pointerout',  () => close.setFillStyle(0x1f2937, 0.9));
    close.on('pointerdown', () => this.scene.start(this.fromScene));
  }

  // ── Tabs ────────────────────────────────────────────────────────────────────

  _drawTabs(W) {
    ['CHALLENGE', 'INFINITE'].forEach((label, i) => {
      const x        = (W / 2) * i + W / 4;
      const isActive = (i === 0) === (this.activeTab === 'challenge');

      const bg = this.add.rectangle(x, HEADER_H + TAB_H / 2, W / 2, TAB_H,
        isActive ? 0x111827 : 0x08090f, 1).setDepth(D).setInteractive({ useHandCursor: true });

      this.add.text(x, HEADER_H + TAB_H / 2, label, {
        fontFamily: 'system-ui, Arial', fontSize: '12px',
        color: isActive ? '#fbbf24' : '#475569',
        fontStyle: isActive ? 'bold' : 'normal',
      }).setOrigin(0.5).setDepth(D + 1);

      if (isActive) {
        this.add.rectangle(x, HEADER_H + TAB_H - 1, W / 2 - 2, 2, 0xfbbf24, 0.85).setDepth(D + 1);
      }

      bg.on('pointerover', () => { if (!isActive) bg.setFillStyle(0x111827, 1); });
      bg.on('pointerout',  () => { if (!isActive) bg.setFillStyle(0x08090f, 1); });
      bg.on('pointerdown', () => {
        const tab = i === 0 ? 'challenge' : 'infinite';
        if (tab !== this.activeTab) this.scene.restart({ tab, from: this.fromScene });
      });
    });

    this.add.rectangle(0, LIST_Y, this.scale.width, 1, 0x1e293b, 1).setOrigin(0, 0).setDepth(D);
  }

  // ── Player section (bottom panel) ───────────────────────────────────────────

  _drawPlayerSection(W, H) {
    // Panel height: remaining space after list area + possible player row
    const panelY = H - 115;
    this.add.rectangle(W / 2, panelY + 57, W, 115, 0x08090f, 0.97).setDepth(D);
    this.add.rectangle(0, panelY, W, 1, 0x1e293b, 1).setOrigin(0, 0).setDepth(D + 1);

    this._rankText = this.add.text(W / 2, panelY + 16, '', {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#64748b',
    }).setOrigin(0.5, 0).setDepth(D + 1);

    const btnY = panelY + 60;
    const btn  = this.add.rectangle(W / 2, btnY, 220, 40, 0x0f2a4a, 0.95)
      .setStrokeStyle(1.5, 0x3b82f6, 0.7).setDepth(D + 1)
      .setInteractive({ useHandCursor: true });
    this._submitLabel = this.add.text(W / 2, btnY, I18n.t('submit_btn'), {
      fontFamily: 'system-ui, Arial', fontSize: '13px',
      color: '#60a5fa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    this._submitBtn = btn;

    btn.on('pointerover', () => btn.setFillStyle(0x1e3a5f, 0.95));
    btn.on('pointerout',  () => btn.setFillStyle(0x0f2a4a, 0.95));
    btn.on('pointerdown', () => this._handleSubmit());

    this._submitStatus = this.add.text(W / 2, btnY + 26, '', {
      fontFamily: 'system-ui, Arial', fontSize: '11px', color: '#22c55e',
    }).setOrigin(0.5, 0).setDepth(D + 1);
  }

  // ── Fetch + render ───────────────────────────────────────────────────────────

  async _fetchAndRender() {
    const W = this.scale.width;
    try {
      // Parallel fetch: top rows + player rank
      const isChallenge  = this.activeTab === 'challenge';
      const playerScore  = isChallenge
        ? LevelStore.calcChallengeScore()
        : LevelStore.getInfiniteBestScore();

      const [rows, playerRank] = await Promise.all([
        isChallenge ? fetchTopChallenge(TOP_N) : fetchTopInfinite(TOP_N),
        playerScore > 0
          ? (isChallenge ? fetchChallengeRank(playerScore) : fetchInfiniteRank(playerScore))
          : Promise.resolve(null),
      ]);

      this._loadingText?.destroy();
      this._renderRows(rows, playerRank, playerScore);
      this._updatePlayerSection(playerRank, playerScore);
    } catch {
      this._loadingText?.setText('Could not load scores.');
    }
  }

  // ── Row renderer ─────────────────────────────────────────────────────────────

  _renderRows(rows, playerRank, playerScore) {
    const W         = this.scale.width;
    const RANK_X    = 18;
    const FLAG_X    = 46;
    const NAME_X    = 82;
    const SCORE_X   = W - 16;
    const isInf     = this.activeTab === 'infinite';
    const playerName = localStorage.getItem('pzm_player_name') || null;

    if (!rows || rows.length === 0) {
      this.add.text(W / 2, LIST_Y + ROW_H * 4, I18n.t('no_scores'), {
        fontFamily: 'system-ui, Arial', fontSize: '13px', color: '#334155',
      }).setOrigin(0.5).setDepth(D);
    } else {
      rows.forEach((row, i) => this._drawRow(row, i, i, RANK_X, FLAG_X, NAME_X, SCORE_X, isInf, playerName, playerScore));
    }

    // Player row outside top 10
    const inTop = playerRank !== null && playerRank <= TOP_N;
    if (playerRank !== null && !inTop && playerScore > 0) {
      const sepY   = LIST_Y + TOP_N * ROW_H;
      const rowIdx = TOP_N + 1; // visual slot after separator

      // ··· separator
      this.add.rectangle(W / 2, sepY + ROW_H / 2, W, ROW_H, 0x07090e, 1).setDepth(D - 1);
      this.add.text(W / 2, sepY + ROW_H / 2, '· · ·', {
        fontFamily: 'system-ui, Arial', fontSize: '14px', color: '#1e293b',
      }).setOrigin(0.5).setDepth(D);

      // Player's own row
      const country = localStorage.getItem('pzm_country') || 'XX';
      const name    = playerName || '—';
      const fakeRow = isInf
        ? { player_name: name, score: playerScore, wave: LevelStore.getInfiniteBestWave(), country_code: country }
        : { player_name: name, total_score: playerScore, country_code: country };

      this._drawRow(fakeRow, rowIdx, playerRank - 1, RANK_X, FLAG_X, NAME_X, SCORE_X, isInf, playerName, playerScore, true);
    }
  }

  _drawRow(row, slot, rankIndex, RANK_X, FLAG_X, NAME_X, SCORE_X, isInf, playerName, playerScore, isPlayer = false) {
    const y      = LIST_Y + slot * ROW_H;
    const midY   = y + ROW_H / 2;
    const isEven = slot % 2 === 0;
    const score  = isInf ? row.score : row.total_score;

    // Highlight player's own row
    const isOwnRow = isPlayer ||
      (playerName && row.player_name === playerName && Number(score) === Number(playerScore));

    const bgColor = isOwnRow ? 0x0f1f0f : (isEven ? 0x0a0e14 : 0x070a10);
    this.add.rectangle(this.scale.width / 2, midY, this.scale.width, ROW_H, bgColor, 1).setDepth(D - 1);

    // Left accent for top 3 or player row
    if (rankIndex < 3) {
      const lc = [0xfbbf24, 0x94a3b8, 0xb45309][rankIndex];
      this.add.rectangle(0, y, 3, ROW_H, lc, 0.85).setOrigin(0, 0).setDepth(D);
    } else if (isOwnRow) {
      this.add.rectangle(0, y, 3, ROW_H, 0x22c55e, 0.85).setOrigin(0, 0).setDepth(D);
    }

    // Rank number
    const rankColors = ['#fbbf24', '#94a3b8', '#b45309'];
    const rankColor  = isOwnRow ? '#22c55e' : (rankColors[rankIndex] || '#334155');
    this.add.text(RANK_X, midY, `#${rankIndex + 1}`, {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: rankColor, fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    // Flag
    this.add.text(FLAG_X, midY, toFlag(row.country_code || 'XX'), {
      fontSize: '17px',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    // Name
    const nameY = isInf ? midY - 7 : midY;
    this.add.text(NAME_X, nameY, (row.player_name || '???').slice(0, 16).toUpperCase(), {
      fontFamily: 'system-ui, Arial', fontSize: '13px',
      color: isOwnRow ? '#86efac' : '#e2e8f0',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    // Wave (infinite)
    if (isInf && row.wave) {
      this.add.text(NAME_X, midY + 8, I18n.t('wave', { n: row.wave }), {
        fontFamily: 'system-ui, Arial', fontSize: '9px', color: '#475569',
      }).setOrigin(0, 0.5).setDepth(D + 1);
    }

    // Score
    this.add.text(SCORE_X, midY, Number(score).toLocaleString(), {
      fontFamily: 'system-ui, Arial', fontSize: '13px',
      color: isOwnRow ? '#86efac' : '#fbbf24', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setDepth(D + 1);
  }

  // ── Bottom player section ────────────────────────────────────────────────────

  _updatePlayerSection(playerRank, playerScore) {
    const isChallenge = this.activeTab === 'challenge';
    const canSubmit   = isChallenge ? LevelStore.isInfiniteUnlocked() : playerScore > 0;

    let rankLine = '';
    if (playerRank !== null && playerScore > 0) {
      rankLine = I18n.t('your_rank', { rank: playerRank, score: Number(playerScore).toLocaleString() });
    } else if (!canSubmit && isChallenge) {
      rankLine = I18n.t('complete_worlds');
    } else {
      rankLine = I18n.t('play_to_board');
    }
    this._rankText.setText(rankLine);

    this._setSubmitEnabled(canSubmit && playerScore > 0);
  }

  _setSubmitEnabled(enabled) {
    if (enabled) {
      this._submitBtn.setInteractive({ useHandCursor: true });
      this._submitLabel.setColor('#60a5fa');
    } else {
      this._submitBtn.disableInteractive();
      this._submitLabel.setColor('#1e3a5f');
    }
  }

  // ── Submit ───────────────────────────────────────────────────────────────────

  async _handleSubmit() {
    // If no identity, send to name builder first (it will save pzm_player_name)
    if (!NameBuilderScene.hasIdentity()) {
      this.scene.start('NameBuilderScene', {
        nextScene: 'LeaderboardScene',
        nextData: { tab: this.activeTab, from: this.fromScene },
        editMode: false,
      });
      return;
    }
    const name = localStorage.getItem('pzm_player_name');
    if (!name) return;

    this._submitLabel.setText(I18n.t('submitting'));
    this._submitBtn.disableInteractive();

    try {
      const country = await getCountryCode();
      let ok = false;

      if (this.activeTab === 'challenge') {
        const total_score = LevelStore.calcChallengeScore();
        ok = await submitChallenge({ player_name: name, total_score, country_code: country });
      } else {
        const score = LevelStore.getInfiniteBestScore();
        const wave  = LevelStore.getInfiniteBestWave();
        ok = await submitInfinite({ player_name: name, score, wave, country_code: country });
      }

      if (ok) {
        this._submitStatus.setText(I18n.t('score_submitted')).setColor('#22c55e');
        this.time.delayedCall(1800, () =>
          this.scene.restart({ tab: this.activeTab, from: this.fromScene })
        );
      } else {
        this._submitStatus.setText(I18n.t('server_error')).setColor('#ef4444');
        this._submitLabel.setText(I18n.t('submit_btn'));
        this._submitBtn.setInteractive({ useHandCursor: true });
      }
    } catch {
      this._submitStatus.setText(I18n.t('network_error')).setColor('#ef4444');
      this._submitLabel.setText(I18n.t('submit_btn'));
      this._submitBtn.setInteractive({ useHandCursor: true });
    }
  }

  _promptName() {
    return new Promise(resolve => {
      const existing = localStorage.getItem('pzm_player_name') || '';

      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:9999;display:flex;align-items:center;justify-content:center;';
      overlay.innerHTML = `
        <div style="background:#0d1117;border:1px solid #3b82f6;border-radius:8px;
                    padding:24px 20px;min-width:300px;max-width:90vw;text-align:center;
                    font-family:system-ui,Arial;">
          <div style="color:#e2e8f0;font-size:14px;margin-bottom:4px;font-weight:bold;">${I18n.t('prompt_name')}</div>
          <div style="color:#475569;font-size:11px;margin-bottom:14px;">${I18n.t('prompt_public')}</div>
          <input id="lb-input" maxlength="20" value="${existing.replace(/"/g, '')}"
            style="width:100%;padding:9px 10px;background:#1e293b;border:1px solid #475569;
                   color:#e2e8f0;font-size:14px;border-radius:4px;box-sizing:border-box;outline:none;"/>
          <div style="display:flex;gap:10px;margin-top:14px;">
            <button id="lb-cancel" style="flex:1;padding:9px;background:#1f2937;border:1px solid #334155;color:#94a3b8;cursor:pointer;border-radius:4px;font-size:13px;">${I18n.t('prompt_cancel')}</button>
            <button id="lb-ok" style="flex:2;padding:9px;background:#1e3a5f;border:1px solid #3b82f6;color:#60a5fa;cursor:pointer;border-radius:4px;font-size:13px;font-weight:bold;">${I18n.t('prompt_confirm')}</button>
          </div>
        </div>`;

      document.body.appendChild(overlay);
      const input = overlay.querySelector('#lb-input');
      input.focus(); input.select();

      const confirm = () => {
        const val = input.value.trim().slice(0, 20);
        document.body.removeChild(overlay);
        if (val) { localStorage.setItem('pzm_player_name', val); resolve(val); }
        else resolve(null);
      };
      const cancel = () => { document.body.removeChild(overlay); resolve(null); };

      overlay.querySelector('#lb-ok').addEventListener('click', confirm);
      overlay.querySelector('#lb-cancel').addEventListener('click', cancel);
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') confirm();
        if (e.key === 'Escape') cancel();
      });
    });
  }
}
